/**
 * Normalize list API payloads that may be a raw array (legacy)
 * or `{ items|jobs|…, pagination }` / header-based totals.
 */

export function toYmd(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getUTCFullYear();
    const m = String(value.getUTCMonth() + 1).padStart(2, '0');
    const d = String(value.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const raw = String(value).trim();
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m ? m[1] : null;
}

export function normalizePagedList(data, { itemsKey = 'items' } = {}) {
  if (Array.isArray(data)) {
    return {
      items: data,
      pagination: {
        total: data.length,
        limit: data.length || DEFAULT_LIST_LIMIT,
        offset: 0,
        page: 1,
        pageCount: 1,
        hasMore: false,
      },
    };
  }
  const items =
    (itemsKey && data?.[itemsKey]) ||
    data?.items ||
    data?.jobs ||
    data?.rows ||
    [];
  const p = data?.pagination || {};
  const total = Number(p.total ?? items.length) || 0;
  const limit = Math.max(1, Number(p.limit ?? items.length) || DEFAULT_LIST_LIMIT);
  const offset = Math.max(0, Number(p.offset ?? 0) || 0);
  return {
    items: Array.isArray(items) ? items : [],
    pagination: {
      total,
      limit,
      offset,
      page: Number(p.page) || Math.floor(offset / limit) + 1,
      pageCount: Number(p.pageCount) || Math.max(1, Math.ceil(total / limit)),
      hasMore: Boolean(p.hasMore ?? offset + limit < total),
    },
  };
}

/** Read X-Limit / X-Offset / X-Total-Count from an axios response. */
export function paginationFromHeaders(response, items = []) {
  const h = response?.headers || {};
  const limit = Number(h['x-limit'] ?? h['X-Limit'] ?? items.length) || items.length || DEFAULT_LIST_LIMIT;
  const offset = Number(h['x-offset'] ?? h['X-Offset'] ?? 0) || 0;
  const totalRaw = h['x-total-count'] ?? h['X-Total-Count'];
  const total =
    totalRaw != null && totalRaw !== ''
      ? Number(totalRaw)
      : offset + items.length + (items.length >= limit ? limit : 0);
  return {
    items,
    pagination: {
      total: Number.isFinite(total) ? total : items.length,
      limit,
      offset,
      page: Math.floor(offset / Math.max(1, limit)) + 1,
      pageCount: Math.max(1, Math.ceil((Number.isFinite(total) ? total : items.length) / Math.max(1, limit))),
      hasMore:
        totalRaw != null
          ? offset + items.length < Number(totalRaw)
          : items.length >= limit,
    },
  };
}

export const DEFAULT_LIST_LIMIT = 20;

/** Slice an in-memory list into a page of ~20 rows. */
export function paginateItems(items, page = 1, limit = DEFAULT_LIST_LIMIT) {
  const list = Array.isArray(items) ? items : [];
  const safeLimit = Math.max(1, Number(limit) || DEFAULT_LIST_LIMIT);
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / safeLimit) || 1);
  const safePage = Math.min(Math.max(1, Number(page) || 1), pageCount);
  const offset = (safePage - 1) * safeLimit;
  return {
    items: list.slice(offset, offset + safeLimit),
    pagination: {
      total,
      limit: safeLimit,
      offset,
      page: safePage,
      pageCount,
      hasMore: safePage < pageCount,
    },
  };
}
